import "./style.scss";
import RenamePopup, {
  InputEditDataType
} from "popups/rename-model/rename-popup";
import { useContext, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { Tooltip } from "antd";
import { message } from "utils/ciq-message";
import { ErrorMessages } from "constants/index";
import FeatureEntityTaskDetailCard from "components/task-detail-card/feature-entity-task-detail-card";
import { ComputeStrategy } from "components/workflow-template-editor/model";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { FeatureTypes } from "entity-app/constants";
import TimelineDurations from "./components/one-timeline-durations";
import {
  TDateBlockPermissions,
  TDateBlockMode,
  TEntityDateblock,
  TGoverningSource,
  TWidthCalculation
} from "./models";
import TimelineIconsOnly from "./components/two-timeline-icons";
import TimelineMilestones from "./components/three-timeline-milestones";
import { APPLY_WORKFLOW_MUTATION } from "./queries/mutation";
import {
  BLOCK_MARGIN,
  computeDateblockOuterWidth,
  getLastLineWidth,
  LEFT_PADDING_ICON_TIMELINE,
  MILESTONES_WIDTH,
  TIMELINE_WIDTH
} from "./layout-constants";

const keyLoadingMessage = "loadingMessagekey";

function EntityDateblock(props: {
  entity: TEntityDateblock | undefined;
  governingSource?: TGoverningSource;
  afterChange?: () => void;
  permissions: TDateBlockPermissions;
  setApiCallLoading?: (isLoading: boolean) => void;
  entityHeaders?: any;
  /** Compact float/deadline layout for material detail Focused View only. */
  materialFocusedLayout?: boolean;
  /** Masks the successor connector through the last milestone circle. */
  designPackageFocusedLayout?: boolean;
  /** Hides the linked-activity row under the last milestone (new dateblock UI). */
  hideGoverningTask?: boolean;
}) {
  const {
    entity,
    governingSource,
    afterChange,
    permissions,
    setApiCallLoading,
    entityHeaders,
    materialFocusedLayout = false,
    designPackageFocusedLayout = false,
    hideGoverningTask = false
  } = props;

  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameDataInput, setRenameDataInput] = useState<InputEditDataType>({
    value: "",
    id: ""
  });

  const { gqlClientForProject } = useContext(ProjectContext);

  const [applyWorkflowMutation] = useCIQMutation(APPLY_WORKFLOW_MUTATION, {
    client: gqlClientForProject
  });

  if (!entity) return null;
  const {
    workflow_instance_milestones: workflowInstanceMilestones,
    feature_workflow_instance: featureWorkflowInstance,
    feature,
    feature_type_id
  } = entity;
  const { show_float: showFloat = true } = feature;

  if (!workflowInstanceMilestones || !featureWorkflowInstance) return null;

  const computeStrategyKey =
    featureWorkflowInstance.project_workflow_template
      ?.dateblock_compute_strategy?.key;

  const dateBlockMode: TDateBlockMode =
    computeStrategyKey === "milestone_commitment_driven"
      ? "milestoneCommitment"
      : computeStrategyKey === ComputeStrategy.COMMITTED_DATE_DRIVEN &&
          ((feature_type_id as FeatureTypes) === FeatureTypes.MATERIALS ||
            (feature_type_id as FeatureTypes) === FeatureTypes.DESIGN_PACKAGE)
        ? "committed"
        : "projected";

  // Sort by sequence_no
  const milestones = workflowInstanceMilestones.sort(
    (a, b) => a.sequence_no - b.sequence_no
  );

  const lastLineWidth = getLastLineWidth(materialFocusedLayout);
  const dateBlockOuterWidth = computeDateblockOuterWidth(milestones, {
    materialFocusedLayout
  });

  const widthCal = {
    blockMargin: BLOCK_MARGIN,
    milestonesWidth: MILESTONES_WIDTH,
    leftpaddingIconTimeline: LEFT_PADDING_ICON_TIMELINE,
    timelineWidth: TIMELINE_WIDTH,
    lastLineWidth
  } as TWidthCalculation;

  const calculateCurrentMilestone = () => {
    for (let index = milestones.length - 1; index >= 0; index = -(1 - index)) {
      const mile = milestones[index];
      if (mile.actual_date) {
        if (index === milestones.length - 1) return undefined;
        return milestones[index + 1];
      }
    }

    return milestones.length ? milestones[0] : undefined;
  };

  const currentMilestone = calculateCurrentMilestone();

  const governingTaskDiv =
    !hideGoverningTask && governingSource?.type === "Linked Activity" ? (
      <Tooltip
        className="govering-db-toolitip"
        styles={{ root: { maxWidth: "300px" } }}
        title={
          <FeatureEntityTaskDetailCard governingSource={governingSource} />
        }
        placement="top"
        data-testid="ed-governing-tooltip"
      >
        <div
          className="h-6 w-full border border-solid  border-gray-400 flex items-center px-1 cursor-pointer text-xs"
          data-testid="ed-governing-task-div"
        >
          <b className="mt-1 mr-1" data-testid="ed-governing-indicator">
            *
          </b>{" "}
          <div className="grow truncate" data-testid="ed-governing-name">
            {governingSource.name || ""}
          </div>
        </div>
      </Tooltip>
    ) : null;

  const applyWorkflowChange = async (data: any) => {
    setApiCallLoading?.(true);
    message.loading({
      content: "Recalculating...",
      key: keyLoadingMessage,
      duration: 60000
    });
    const input = [
      {
        feature_instance_id: featureWorkflowInstance.feature_instance_id,
        workflow_instance_id: featureWorkflowInstance.id,
        ...data
      }
    ];

    const res = await applyWorkflowMutation(
      {
        variables: {
          input,
          metadata: {
            request_source: "DETAIL_PAGE"
          }
        }
      },
      { showError: false }
    );
    if (res.data) {
      message.success({
        content: "Updated successfully!",
        key: keyLoadingMessage,
        duration: 3
      });

      if (afterChange) {
        afterChange();
      }
    } else {
      message.error({
        content: res?.error?.message || ErrorMessages.SomethingWentWrong,
        key: keyLoadingMessage,
        duration: 3
      });
    }
    setApiCallLoading?.(false);
  };

  const editOffsetDateBlock = async (newOffset: string, id: string) => {
    const set = {} as any;
    if (renameDataInput.identityName === "WorkflowInstanceOffset") {
      set.duration = newOffset;

      applyWorkflowChange({
        offsets: [
          {
            id,
            duration: Number(newOffset)
          }
        ]
      });
    }

    if (renameDataInput.identityName === "WorkflowInstanceFloat") {
      applyWorkflowChange({
        pre_WF_start_float: Number(newOffset)
      });
    }
  };

  return (
    <div
      style={{ width: dateBlockOuterWidth }}
      className="flex flex-col shrink-0 py-8 justify-center entity-date-block px-5"
      data-testid="ed-root"
    >
      <TimelineDurations
        milestones={milestones}
        widthCal={widthCal}
        featureWorkflowInstance={featureWorkflowInstance}
        setIsRenameModalOpen={setIsRenameModalOpen}
        setRenameDataInput={setRenameDataInput}
        showFloat={showFloat}
        permissions={permissions}
        dateBlockMode={dateBlockMode}
        data-testid="ed-timeline-durations"
      />
      <TimelineIconsOnly
        applyWorkflowChange={applyWorkflowChange}
        milestones={milestones}
        currentMilestone={currentMilestone}
        widthCal={widthCal}
        featureWorkflowInstance={featureWorkflowInstance}
        showFloat={showFloat}
        setIsRenameModalOpen={setIsRenameModalOpen}
        setRenameDataInput={setRenameDataInput}
        governingSource={governingSource}
        permissions={permissions}
        entityHeaders={entityHeaders}
        materialFocusedLayout={materialFocusedLayout}
        designPackageFocusedLayout={designPackageFocusedLayout}
        data-testid="ed-timeline-icons"
      />
      <TimelineMilestones
        applyWorkflowChange={applyWorkflowChange}
        milestones={milestones}
        currentMilestone={currentMilestone}
        widthCal={widthCal}
        featureWorkflowInstance={featureWorkflowInstance}
        governingTaskDiv={governingTaskDiv}
        permissions={permissions}
        dateBlockMode={dateBlockMode}
        data-testid="ed-timeline-milestones"
      />

      {isRenameModalOpen && (
        <RenamePopup
          isModalOpen={isRenameModalOpen}
          setIsModalOpen={setIsRenameModalOpen}
          inputData={renameDataInput}
          onOk={editOffsetDateBlock}
          title="Edit duration"
          okName="Save"
          data-testid="ed-rename-popup"
        />
      )}
    </div>
  );
}
export default EntityDateblock;
